import { isJsonObject, jsonObject, recordedString } from '@/types/json.types'
import type { UpdateTenantInput } from '@/types/tenant.types'

/**
 * Tenant Settings Page
 * Comprehensive organization/firm profile management
 * Accessible to Owner and Admin roles
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2,
  Mail,
  MapPin,
  Globe,
  CreditCard,
  Users,
  FileText,
  Save,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  Crown,
  ChevronRight,
  HardDrive,
  Upload,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from '@/store/toastStore'
import { getTenantService } from '@/services/tenant'
import type { EffectiveEntitlements, PlanCode, Tenant, Subscription } from '@/types/tenant.types'

// ============================================================================
// TYPES
// ============================================================================

interface FirmProfile {
  tenantName: string
  firmGstin: string
  firmPan: string
  firmEmail: string
  firmPhone: string
  firmAddress: {
    line1: string
    line2: string
    city: string
    state: string
    pincode: string
    country: string
  }
  website: string
  establishedYear: string
  registrationNumber: string
}

interface FirmSettings {
  timezone: string
  dateFormat: string
  currency: string
  financialYearStart: string
  defaultLanguage: string
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Puducherry',
  'Chandigarh',
]

const PLAN_DETAILS: Record<PlanCode, { color: string }> = {
  trial: {
    color: 'badge-ghost',
  },
  starter: {
    color: 'badge-accent',
  },
  professional: {
    color: 'badge-secondary',
  },
  enterprise: {
    color: 'badge-primary',
  },
}

const FEATURE_LABELS: Record<string, string> = {
  basicReports: 'Basic reports',
  advancedReports: 'Advanced reports',
  bulkOperations: 'Bulk operations',
  apiAccess: 'API access',
  sso: 'Single sign-on',
  customBranding: 'Custom branding',
  emailSupport: 'Email support',
  prioritySupport: 'Priority support',
  dedicatedSupport: 'Dedicated support',
}

// ============================================================================
// SECTION CARD COMPONENT
// ============================================================================

function SectionCard({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ElementType
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="card card-elevated p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="p-3 rounded-lg bg-primary-100 dark:bg-primary-900/30">
          <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
          {description && <p className="text-sm text-text-secondary mt-1">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TenantSettingsPage() {
  const { user: currentUser, hasPermission } = useAuth()
  const canManageSettings = hasPermission('txh.settings.update')
  const canManageBilling = hasPermission('txh.settings.billing')

  // Profile state
  const [profile, setProfile] = useState<FirmProfile>({
    tenantName: currentUser?.tenantName || '',
    firmGstin: '',
    firmPan: '',
    firmEmail: '',
    firmPhone: '',
    firmAddress: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: '',
      country: '',
    },
    website: '',
    establishedYear: '',
    registrationNumber: '',
  })

  // Settings state
  const [settings, setSettings] = useState<FirmSettings>({
    timezone: '',
    dateFormat: '',
    currency: '',
    financialYearStart: '',
    defaultLanguage: '',
  })

  const [pendingPatch, setPendingPatch] = useState<UpdateTenantInput>({})
  const [recordedSettings, setRecordedSettings] = useState<unknown>(null)
  const [recordedAddress, setRecordedAddress] = useState<unknown>(null)
  const settingsEditable = recordedSettings == null || isJsonObject(recordedSettings)
  const addressEditable = recordedAddress == null || isJsonObject(recordedAddress)
  const [hasChanges, setHasChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [activeSection, setActiveSection] = useState<'profile' | 'settings' | 'subscription'>(
    'profile'
  )

  // Subscription panel starts empty and is hydrated only from persisted data.
  const [subscription, setSubscription] = useState({
    planCode: (currentUser?.planCode || 'trial') as PlanCode,
    planName: 'Loading',
    status: '',
    billingCycle: '',
    nextBillingDate: '',
    currentUsers: 0,
    maxUsers: 0,
    currentClients: 0,
    maxClients: 0,
    storageUsed: 0,
    storageLimit: 0,
    features: [] as string[],
  })

  // Map a loaded Tenant into the local profile/settings form shapes.
  function applyTenant(tenant: Tenant) {
    const loaded = jsonObject(tenant.settings)
    const address = jsonObject(tenant.firmAddress)
    const setting = (camel: string, snake: string) =>
      recordedString(
        Object.prototype.hasOwnProperty.call(loaded, snake) ? loaded[snake] : loaded[camel]
      )
    setRecordedSettings(tenant.settings)
    setRecordedAddress(tenant.firmAddress)
    setProfile({
      tenantName: tenant.tenantName ?? '',
      firmGstin: tenant.firmGstin ?? '',
      firmPan: tenant.firmPan ?? '',
      firmEmail: tenant.firmEmail ?? '',
      firmPhone: tenant.firmPhone ?? '',
      website: setting('firmWebsite', 'firm_website'),
      establishedYear: setting('establishedYear', 'established_year'),
      registrationNumber: setting('registrationNumber', 'registration_number'),
      firmAddress: {
        line1: recordedString(address.line1),
        line2: recordedString(address.line2),
        city: recordedString(address.city),
        state: recordedString(address.state),
        pincode: recordedString(address.pincode),
        country: recordedString(address.country),
      },
    })
    setSettings({
      timezone: setting('timezone', 'timezone'),
      dateFormat: setting('dateFormat', 'date_format'),
      currency: setting('currency', 'currency'),
      financialYearStart: setting('financialYearStart', 'financial_year_start'),
      defaultLanguage: setting('defaultLanguage', 'default_language'),
    })
    setPendingPatch({})
  }

  function applySubscription(sub: Subscription) {
    setSubscription(prev => ({
      ...prev,
      planCode: sub.planCode,
      status: sub.status,
      billingCycle: sub.billingCycle ?? prev.billingCycle,
      nextBillingDate: sub.expiresAt ?? prev.nextBillingDate,
    }))
  }

  function applyEntitlements(entitlements: EffectiveEntitlements) {
    setSubscription(prev => ({
      ...prev,
      planCode: entitlements.planCode,
      planName: entitlements.planName,
      status: entitlements.subscriptionStatus,
      currentUsers: entitlements.usage.users ?? 0,
      maxUsers: entitlements.limits.maxUsers ?? 0,
      currentClients: entitlements.usage.clients ?? 0,
      maxClients: entitlements.limits.maxClients ?? 0,
      storageUsed: (entitlements.usage.storageBytes ?? 0) / 1024 ** 3,
      storageLimit: (entitlements.limits.maxStorageBytes ?? 0) / 1024 ** 3,
      features: Object.entries(entitlements.features)
        .filter(([, enabled]) => enabled)
        .map(([key]) => FEATURE_LABELS[key] ?? key),
    }))
  }

  // Load the firm profile + subscription on mount.
  useEffect(() => {
    if (!currentUser?.tenantId) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    const svc = getTenantService()
    Promise.allSettled([
      svc.get(currentUser.tenantId),
      svc.getSubscription(currentUser.tenantId),
      svc.getEffectiveEntitlements(currentUser.tenantId),
    ])
      .then(([tenantResult, subResult, entitlementResult]) => {
        if (cancelled) return
        if (tenantResult.status === 'fulfilled') applyTenant(tenantResult.value)
        if (subResult.status === 'fulfilled' && subResult.value) {
          applySubscription(subResult.value)
        }
        if (entitlementResult.status === 'fulfilled') {
          applyEntitlements(entitlementResult.value)
        }
        setHasChanges(false)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.tenantId])

  // Update profile
  const updateProfile = (key: Exclude<keyof FirmProfile, 'firmAddress'>, value: string) => {
    if (!canManageSettings) return
    if (key === 'website' || key === 'establishedYear' || key === 'registrationNumber') {
      if (!settingsEditable) return
      const field = key === 'website' ? 'firmWebsite' : key
      setPendingPatch(prev => ({ ...prev, settings: { ...prev.settings, [field]: value } }))
    } else {
      setPendingPatch(prev => ({ ...prev, [key]: value }))
    }
    setProfile(prev => ({ ...prev, [key]: value }))
    setHasChanges(true)
  }

  // Update address
  const updateAddress = (key: keyof FirmProfile['firmAddress'], value: string) => {
    if (!canManageSettings || !addressEditable) return
    setPendingPatch(prev => ({ ...prev, firmAddress: { ...prev.firmAddress, [key]: value } }))
    setProfile(prev => ({
      ...prev,
      firmAddress: { ...prev.firmAddress, [key]: value },
    }))
    setHasChanges(true)
  }

  // Update settings
  const updateSettings = (key: keyof FirmSettings, value: string) => {
    if (!canManageSettings || !settingsEditable) return
    setPendingPatch(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }))
    setSettings(prev => ({ ...prev, [key]: value }))
    setHasChanges(true)
  }

  // Save changes — persists the firm profile + regional settings via the tenant
  // service (PATCH /tenants/{id}).
  const handleSave = async () => {
    if (!canManageSettings || !currentUser?.tenantId) {
      toast.error('Could not save settings', 'No organization is associated with your account.')
      return
    }
    setIsSaving(true)
    try {
      const updated = await getTenantService().update(currentUser.tenantId, pendingPatch)
      applyTenant(updated)
      setHasChanges(false)
      toast.success('Organization settings saved', 'Your firm profile has been updated.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again.'
      toast.error('Could not save settings', message)
    } finally {
      setIsSaving(false)
    }
  }

  // Reset changes — re-fetch the persisted profile, discarding edits.
  const handleReset = () => {
    if (!canManageSettings) return
    if (!window.confirm('Discard all unsaved changes?')) return
    if (!currentUser?.tenantId) {
      setHasChanges(false)
      return
    }
    getTenantService()
      .get(currentUser.tenantId)
      .then(tenant => {
        applyTenant(tenant)
        setHasChanges(false)
      })
      .catch(() => setHasChanges(false))
  }

  const planDetails = PLAN_DETAILS[subscription.planCode]

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Organization Settings</h1>
          <p className="text-text-secondary mt-1">
            Manage your firm profile, settings, and subscription
          </p>
        </div>
        {canManageSettings && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="btn btn-ghost btn-sm gap-2"
              disabled={!hasChanges || isSaving}
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn btn-primary btn-sm gap-2"
              disabled={!hasChanges || isSaving || isLoading}
            >
              {isSaving ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        )}
      </div>

      {/* Section Tabs */}
      <div className="flex border-b border-surface-overlay">
        {[
          { id: 'profile', label: 'Firm Profile', icon: Building2 },
          { id: 'settings', label: 'Regional Settings', icon: Globe },
          { id: 'subscription', label: 'Subscription', icon: CreditCard },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSection(tab.id as any)}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === tab.id
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Firm Profile Section */}
      {(!settingsEditable || !addressEditable) && (
        <div className="card p-4 text-sm text-text-secondary">
          <p>
            Some recorded settings or address values require review before editing. Other profile
            fields remain editable.
          </p>
          {!settingsEditable && (
            <pre className="whitespace-pre-wrap break-words">
              Settings: {JSON.stringify(recordedSettings)}
            </pre>
          )}
          {!addressEditable && (
            <pre className="whitespace-pre-wrap break-words">
              Address: {JSON.stringify(recordedAddress)}
            </pre>
          )}
        </div>
      )}

      {activeSection === 'profile' && (
        <div className="space-y-6">
          {/* Basic Information */}
          <SectionCard
            icon={Building2}
            title="Firm Information"
            description="Basic details about your firm"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Firm Name *
                </label>
                <input
                  type="text"
                  value={profile.tenantName}
                  onChange={e => updateProfile('tenantName', e.target.value)}
                  className="input input-bordered w-full"
                  disabled={!canManageSettings || isSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">GSTIN</label>
                <input
                  type="text"
                  value={profile.firmGstin}
                  onChange={e => updateProfile('firmGstin', e.target.value)}
                  className="input input-bordered w-full font-mono uppercase"
                  placeholder="27AABCT1234K1ZK"
                  disabled={!canManageSettings || isSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">PAN</label>
                <input
                  type="text"
                  value={profile.firmPan}
                  onChange={e => updateProfile('firmPan', e.target.value)}
                  className="input input-bordered w-full font-mono uppercase"
                  placeholder="AABCT1234K"
                  disabled={!canManageSettings || isSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Registration Number
                </label>
                <input
                  type="text"
                  value={profile.registrationNumber}
                  onChange={e => updateProfile('registrationNumber', e.target.value)}
                  className="input input-bordered w-full"
                  disabled={!canManageSettings || isSaving || !settingsEditable}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Established Year
                </label>
                <input
                  type="text"
                  value={profile.establishedYear}
                  onChange={e => updateProfile('establishedYear', e.target.value)}
                  className="input input-bordered w-full"
                  disabled={!canManageSettings || isSaving || !settingsEditable}
                />
              </div>
            </div>
          </SectionCard>

          {/* Contact Information */}
          <SectionCard
            icon={Mail}
            title="Contact Information"
            description="How clients and partners can reach you"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Email</label>
                <input
                  type="email"
                  value={profile.firmEmail}
                  onChange={e => updateProfile('firmEmail', e.target.value)}
                  className="input input-bordered w-full"
                  disabled={!canManageSettings || isSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Phone</label>
                <input
                  type="tel"
                  value={profile.firmPhone}
                  onChange={e => updateProfile('firmPhone', e.target.value)}
                  className="input input-bordered w-full"
                  disabled={!canManageSettings || isSaving}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary mb-2">Website</label>
                <input
                  type="url"
                  value={profile.website}
                  onChange={e => updateProfile('website', e.target.value)}
                  className="input input-bordered w-full"
                  placeholder="https://www.yourfirm.com"
                  disabled={!canManageSettings || isSaving || !settingsEditable}
                />
              </div>
            </div>
          </SectionCard>

          {/* Address */}
          <SectionCard
            icon={MapPin}
            title="Office Address"
            description="Your firm's registered address"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={profile.firmAddress.line1}
                  onChange={e => updateAddress('line1', e.target.value)}
                  className="input input-bordered w-full"
                  disabled={!canManageSettings || isSaving || !addressEditable}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={profile.firmAddress.line2}
                  onChange={e => updateAddress('line2', e.target.value)}
                  className="input input-bordered w-full"
                  disabled={!canManageSettings || isSaving || !addressEditable}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">City</label>
                <input
                  type="text"
                  value={profile.firmAddress.city}
                  onChange={e => updateAddress('city', e.target.value)}
                  className="input input-bordered w-full"
                  disabled={!canManageSettings || isSaving || !addressEditable}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">State</label>
                <select
                  value={profile.firmAddress.state}
                  onChange={e => updateAddress('state', e.target.value)}
                  className="select select-bordered w-full"
                  disabled={!canManageSettings || isSaving || !addressEditable}
                >
                  {INDIAN_STATES.map(state => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">PIN Code</label>
                <input
                  type="text"
                  value={profile.firmAddress.pincode}
                  onChange={e => updateAddress('pincode', e.target.value)}
                  className="input input-bordered w-full"
                  maxLength={6}
                  disabled={!canManageSettings || isSaving || !addressEditable}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Country</label>
                <input
                  type="text"
                  value={profile.firmAddress.country}
                  className="input input-bordered w-full"
                  disabled
                />
              </div>
            </div>
          </SectionCard>

          {/* Logo Upload */}
          <SectionCard
            icon={Upload}
            title="Firm Logo"
            description="Upload your firm's logo for branding"
          >
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center border-2 border-dashed border-primary-300 dark:border-primary-700">
                <Building2 className="w-12 h-12 text-primary-400" />
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={!canManageSettings || isSaving}
                >
                  Upload Logo
                </button>
                <p className="text-xs text-text-tertiary mt-2">
                  PNG, JPG up to 2MB. Recommended: 200x200px
                </p>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* Regional Settings Section */}
      {activeSection === 'settings' && (
        <div className="space-y-6">
          <SectionCard
            icon={Globe}
            title="Regional Settings"
            description="Configure regional preferences for your firm"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Timezone</label>
                <select
                  value={settings.timezone}
                  onChange={e => updateSettings('timezone', e.target.value)}
                  className="select select-bordered w-full"
                  disabled={!canManageSettings || isSaving || !settingsEditable}
                >
                  <option value="">Not recorded</option>
                  {settings.timezone &&
                    !['Asia/Kolkata', 'Asia/Dubai', 'Europe/London', 'America/New_York'].includes(
                      settings.timezone
                    ) && <option value={settings.timezone}>{settings.timezone}</option>}
                  <option value="Asia/Kolkata">India Standard Time (IST)</option>
                  <option value="Asia/Dubai">Gulf Standard Time (GST)</option>
                  <option value="Europe/London">British Time (GMT/BST)</option>
                  <option value="America/New_York">Eastern Time (EST/EDT)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Date Format
                </label>
                <select
                  value={settings.dateFormat}
                  onChange={e => updateSettings('dateFormat', e.target.value)}
                  className="select select-bordered w-full"
                  disabled={!canManageSettings || isSaving || !settingsEditable}
                >
                  <option value="">Not recorded</option>
                  {settings.dateFormat &&
                    !['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].includes(settings.dateFormat) && (
                      <option value={settings.dateFormat}>{settings.dateFormat}</option>
                    )}
                  <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2026)</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2026)</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD (2026-12-31)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Currency</label>
                <select
                  value={settings.currency}
                  onChange={e => updateSettings('currency', e.target.value)}
                  className="select select-bordered w-full"
                  disabled={!canManageSettings || isSaving || !settingsEditable}
                >
                  <option value="">Not recorded</option>
                  {settings.currency &&
                    !['INR', 'USD', 'EUR', 'GBP'].includes(settings.currency) && (
                      <option value={settings.currency}>{settings.currency}</option>
                    )}
                  <option value="INR">Indian Rupee (₹)</option>
                  <option value="USD">US Dollar ($)</option>
                  <option value="EUR">Euro (€)</option>
                  <option value="GBP">British Pound (£)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Financial Year Start
                </label>
                <select
                  value={settings.financialYearStart}
                  onChange={e => updateSettings('financialYearStart', e.target.value)}
                  className="select select-bordered w-full"
                  disabled={!canManageSettings || isSaving || !settingsEditable}
                >
                  <option value="">Not recorded</option>
                  {settings.financialYearStart &&
                    !['April', 'January', 'July'].includes(settings.financialYearStart) && (
                      <option value={settings.financialYearStart}>
                        {settings.financialYearStart}
                      </option>
                    )}
                  <option value="April">April (Indian FY)</option>
                  <option value="January">January (Calendar Year)</option>
                  <option value="July">July (Australian FY)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Default Language
                </label>
                <select
                  value={settings.defaultLanguage}
                  onChange={e => updateSettings('defaultLanguage', e.target.value)}
                  className="select select-bordered w-full"
                  disabled={!canManageSettings || isSaving || !settingsEditable}
                >
                  <option value="">Not recorded</option>
                  {settings.defaultLanguage &&
                    !['en-IN', 'en-US', 'hi'].includes(settings.defaultLanguage) && (
                      <option value={settings.defaultLanguage}>{settings.defaultLanguage}</option>
                    )}
                  <option value="en-IN">English (India)</option>
                  <option value="en-US">English (US)</option>
                  <option value="hi">Hindi</option>
                </select>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* Subscription Section */}
      {activeSection === 'subscription' && (
        <div className="space-y-6">
          {/* Current Plan */}
          <div className="card card-elevated p-6 border-l-4 border-primary-500">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                  <Crown className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-text-primary">
                      {subscription.planName} Plan
                    </h2>
                    <span className={`badge ${planDetails.color}`}>
                      {subscription.status === 'active' ? 'Active' : subscription.status}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary mt-1">
                    Billed {subscription.billingCycle} • Next billing:{' '}
                    {new Date(subscription.nextBillingDate).toLocaleDateString('en-IN')}
                  </p>
                </div>
              </div>
              {canManageBilling && (
                <button type="button" className="btn btn-outline btn-sm">
                  Upgrade Plan
                </button>
              )}
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {subscription.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" />
                  <span className="text-text-secondary">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Usage Stats */}
          <SectionCard
            icon={FileText}
            title="Usage Statistics"
            description="Current usage against your plan limits"
          >
            <div className="space-y-6">
              {/* Users */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-text-tertiary" />
                    <span className="text-sm font-medium text-text-primary">Team Members</span>
                  </div>
                  <span className="text-sm text-text-secondary">
                    {subscription.currentUsers} / {subscription.maxUsers}
                  </span>
                </div>
                <div className="w-full h-2 bg-surface-overlay rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full"
                    style={{
                      width: `${(subscription.currentUsers / subscription.maxUsers) * 100}%`,
                    }}
                  />
                </div>
              </div>

              {/* Clients */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-text-tertiary" />
                    <span className="text-sm font-medium text-text-primary">Clients</span>
                  </div>
                  <span className="text-sm text-text-secondary">
                    {subscription.currentClients} / {subscription.maxClients}
                  </span>
                </div>
                <div className="w-full h-2 bg-surface-overlay rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full"
                    style={{
                      width: `${(subscription.currentClients / subscription.maxClients) * 100}%`,
                    }}
                  />
                </div>
              </div>

              {/* Storage */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-4 h-4 text-text-tertiary" />
                    <span className="text-sm font-medium text-text-primary">Storage</span>
                  </div>
                  <span className="text-sm text-text-secondary">
                    {subscription.storageUsed} GB / {subscription.storageLimit} GB
                  </span>
                </div>
                <div className="w-full h-2 bg-surface-overlay rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-500 rounded-full"
                    style={{
                      width: `${(subscription.storageUsed / subscription.storageLimit) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Quick Links */}
          <SectionCard
            icon={CreditCard}
            title="Billing & Invoices"
            description="Manage payment methods and view invoices"
          >
            <div className="space-y-2">
              <Link
                to="/billing"
                className="flex items-center justify-between p-3 rounded-lg border border-surface-overlay hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">Payment Methods</p>
                    <p className="text-xs text-text-tertiary">Manage cards and bank accounts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
              </Link>

              <Link
                to="/invoices"
                className="flex items-center justify-between p-3 rounded-lg border border-surface-overlay hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">Invoices</p>
                    <p className="text-xs text-text-tertiary">View and download past invoices</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
              </Link>
            </div>
          </SectionCard>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="card card-elevated p-4 shadow-2xl flex items-center gap-4 border-l-4 border-orange-500">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-text-primary">You have unsaved changes</span>
            <div className="flex gap-2">
              <button type="button" onClick={handleReset} className="btn btn-ghost btn-sm">
                Discard
              </button>
              <button type="button" onClick={handleSave} className="btn btn-primary btn-sm">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
